import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import CaptchaWidget from "@/components/CaptchaWidget";
import { validateEmail, validateIndianPhone } from "@/utils/validation";
import { ContactSection } from "@/components/leads/ContactSection";
import { AssignmentSection } from "@/components/leads/AssignmentSection";
import { QualificationSection } from "@/components/leads/QualificationSection";
import { SiteVisitSection } from "@/components/leads/SiteVisitSection";
import { LostReasonSection } from "@/components/leads/LostReasonSection";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LeadForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const [formData, setFormData] = useState({
    name: "", email: "", phone: "",
    property_id: "", source: "website",
    assigned_to: user.role === "agent" ? user.id : "",
    notes: "", status: "new_web_lead",
    budget_min: "", budget_max: "",
    preferred_location: "", property_type_preference: "", bhk_preference: "",
    priority: "medium",
    investment_or_selfuse: "", timeline_to_purchase: "", family_size: "", financing: "",
    lost_reason: "", lost_notes: "",
    site_visit_date: "", site_visit_location: "", site_visit_feedback: "",
    pitched_properties: [],
  });
  const [properties, setProperties] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaError, setCaptchaError] = useState("");
  const recaptchaRef = useRef(null);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const [propertiesRes, agentsRes] = await Promise.all([
        axios.get(`${API}/properties`, { headers }),
        user.role === "admin" ? axios.get(`${API}/users`, { headers }) : Promise.resolve({ data: [] }),
      ]);
      setProperties(propertiesRes.data);
      setAgents(agentsRes.data.filter((u) => ['agent', 'team_leader'].includes(u.role)));

      if (isEdit) {
        const leadRes = await axios.get(`${API}/leads/${id}`, { headers });
        const lead = leadRes.data;
        setFormData({
          name: lead.name || "", email: lead.email || "", phone: lead.phone || "",
          property_id: lead.property_id || "", source: lead.source || "website",
          assigned_to: lead.assigned_to || "", notes: lead.notes || "",
          status: lead.status || "new_web_lead",
          budget_min: lead.budget_min || "", budget_max: lead.budget_max || "",
          preferred_location: lead.preferred_location || "",
          property_type_preference: lead.property_type_preference || "",
          bhk_preference: lead.bhk_preference || "", priority: lead.priority || "medium",
          investment_or_selfuse: lead.investment_or_selfuse || "",
          timeline_to_purchase: lead.timeline_to_purchase || "",
          family_size: lead.family_size || "", financing: lead.financing || "",
          lost_reason: lead.lost_reason || "", lost_notes: lead.lost_notes || "",
          site_visit_date: lead.site_visit_date ? lead.site_visit_date.split('T')[0] : "",
          site_visit_location: lead.site_visit_location || "",
          site_visit_feedback: lead.site_visit_feedback || "",
          pitched_properties: lead.pitched_properties || [],
        });
      }
    } catch (error) {
      toast.error("Failed to load data");
      navigate("/leads");
    } finally {
      setInitialLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'email') setEmailError(value ? (validateEmail(value).error || "") : "");
    if (name === 'phone') setPhoneError(value ? (validateIndianPhone(value).error || "") : "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const emailResult = validateEmail(formData.email);
    if (!emailResult.isValid) { toast.error(emailResult.error); setEmailError(emailResult.error); return; }
    const phoneResult = validateIndianPhone(formData.phone);
    if (!phoneResult.isValid) { toast.error(phoneResult.error); setPhoneError(phoneResult.error); return; }
    if (!isEdit && !captchaToken) { toast.error("Please complete the CAPTCHA verification"); setCaptchaError("Please verify you are not a robot"); return; }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const leadData = { ...formData, phone: phoneResult.formatted || formData.phone, property_id: formData.property_id || null };

      if (isEdit) {
        await axios.put(`${API}/leads/${id}`, leadData, { headers });
        toast.success("Lead updated successfully");
      } else {
        await axios.post(`${API}/leads`, leadData, { headers });
        toast.success("Lead created successfully");
        recaptchaRef.current?.reset();
        setCaptchaToken("");
      }
      navigate("/leads");
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail
        : Array.isArray(detail) ? detail.map(e => e.msg || String(e)).join(', ')
        : "Failed to save lead";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return <Layout><div className="flex items-center justify-center h-96"><div className="spinner" /></div></Layout>;
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto fade-in">
        <Button data-testid="back-button" onClick={() => navigate("/leads")} variant="ghost" className="mb-6 -ml-4">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Leads
        </Button>

        <div className="bg-white rounded-md border border-slate-200 shadow-sm p-8">
          <h1 className="text-3xl font-semibold text-slate-900 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            {isEdit ? "Edit Lead" : "Add New Lead"}
          </h1>

          <form onSubmit={handleSubmit} className="space-y-6">
            <ContactSection formData={formData} handleChange={handleChange} emailError={emailError} phoneError={phoneError} user={user} isEdit={isEdit} />
            <AssignmentSection formData={formData} handleChange={handleChange} properties={properties} agents={agents} user={user} isEdit={isEdit} />
            <QualificationSection formData={formData} handleChange={handleChange} isEdit={isEdit} />
            {isEdit && <SiteVisitSection formData={formData} handleChange={handleChange} />}
            {isEdit && <LostReasonSection formData={formData} handleChange={handleChange} />}

            {/* Notes */}
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea id="notes" name="notes" data-testid="lead-notes-input" value={formData.notes} onChange={handleChange} className="mt-1 rounded-sm min-h-[100px]" placeholder="Add any additional notes about this lead..." />
            </div>

            {/* CAPTCHA - New leads only */}
            {!isEdit && (
              <div className="space-y-2 pt-4 border-t">
                <Label>Verify You're Human *</Label>
                <div className="flex justify-center">
                  <CaptchaWidget ref={recaptchaRef} onChange={(token) => { setCaptchaToken(token || ""); setCaptchaError(""); }} onExpired={() => { setCaptchaToken(""); setCaptchaError("CAPTCHA expired. Please verify again."); }} />
                </div>
                {captchaError && (
                  <p className="text-red-500 text-xs text-center flex items-center justify-center gap-1">
                    <AlertCircle className="h-3 w-3" /> {captchaError}
                  </p>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-4 pt-4">
              <Button type="button" variant="outline" onClick={() => navigate("/leads")} className="rounded-sm">Cancel</Button>
              <Button data-testid="submit-lead-button" type="submit" disabled={loading || (!isEdit && !captchaToken)} className="bg-blue-900 hover:bg-blue-800 text-white rounded-sm disabled:opacity-50 disabled:cursor-not-allowed">
                {loading ? <div className="spinner mx-auto" style={{ width: '20px', height: '20px', borderWidth: '2px' }} /> : isEdit ? "Update Lead" : "Create Lead"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
}
